import { Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { JSONValue } from "@/components/jsonSchemaForm/types";

interface PriceModelInputRevertProps {
  /** The last-saved value the control restores. */
  savedValue: JSONValue;
  /** Field label, used to name the trigger for screen readers. */
  fieldLabel: string;
  /** Puts the last-saved value back into the field. */
  onRevert: () => void;
}

/**
 * Undo affordance restoring a price model input to its last-saved value, shown
 * on edited fields whose saved value carries the agent's reasoning.
 * @param props - The saved value, the label of the field it belongs to and the revert handler
 * @returns The revert button and its explanatory tooltip
 */
export function PriceModelInputRevert({
  savedValue,
  fieldLabel,
  onRevert,
}: PriceModelInputRevertProps) {
  const preview = formatSavedValue(savedValue);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Revert ${fieldLabel} to the saved value`}
          className="h-5 w-5 shrink-0 text-muted-foreground/70 hover:text-muted-foreground [&_svg]:size-3"
          onClick={onRevert}
        >
          <Undo2 />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-56">
        {preview
          ? `Revert to the saved value: ${preview}`
          : "Revert to the saved value"}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Formats a saved leaf value for the tooltip.
 * @param value - The value the control would restore
 * @returns The value as text, or undefined when it has no readable form
 */
function formatSavedValue(value: JSONValue): string | undefined {
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (typeof value === "string" && value.trim() !== "") {
    return value;
  }
  return undefined;
}
